/**
 * DelinquencyPage
 * Delinquency analysis: summary cards and week-bucket distribution table
 */

import {
  Users,
  Banknote,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/data-display/StatCard';
import { ROUTES } from '@/constants';
import { HelpButton } from '@/features/help';
import { useCollectionDashboard } from '@/features/collection/hooks';
import { BucketDistributionTable } from '@/features/collection/components/DelinquencyTable';
import { formatCurrencyMXN, formatPercentage } from '@/features/collection/types';
import { formatNumber } from '@/utils/formatters';

// ─── Constants ───────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
  { label: 'Morosidad', path: ROUTES.DELINQUENCY },
];

// ─── Skeleton Loading State ──────────────────────────────────────────────────

function DelinquencySkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={`stat-skeleton-${i.toString()}`}
            className="rounded-lg border border-border bg-card p-5"
          >
            <Skeleton variant="text" width="60%" height={14} />
            <div className="mt-3">
              <Skeleton variant="text" width="50%" height={28} />
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border bg-card p-6">
        <Skeleton variant="text" width="40%" height={18} />
        <div className="mt-4 space-y-3">
          <Skeleton variant="rectangular" height={150} />
        </div>
      </div>
    </div>
  );
}

// ─── Error State ─────────────────────────────────────────────────────────────

function DelinquencyError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar el analisis de morosidad
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function DelinquencyPage() {
  const { delinquency, isLoading, isError, refetchAll } =
    useCollectionDashboard();

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Morosidad"
          subtitle="Analisis de mora por antiguedad"
          breadcrumbs={BREADCRUMBS}
        />
        <DelinquencySkeleton />
      </div>
    );
  }

  if (isError || !delinquency) {
    return (
      <div>
        <PageHeader
          title="Morosidad"
          subtitle="Analisis de mora por antiguedad"
          breadcrumbs={BREADCRUMBS}
        />
        <DelinquencyError onRetry={refetchAll} />
      </div>
    );
  }

  // Compute total outstanding from by_weeks values
  const totalOutstanding = Object.values(delinquency.by_weeks ?? {}).reduce(
    (s, b) => s + b.total_outstanding_mxn,
    0,
  );

  return (
    <div>
      <PageHeader
        title="Morosidad"
        subtitle="Analisis de mora por antiguedad"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="delinquency" />
            <Button onClick={refetchAll}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* ── Summary Cards ────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            title="Total Morosos"
            value={formatNumber(delinquency.total_delinquent)}
            icon={Users}
          />
          <StatCard
            title="Monto Total en Mora"
            value={formatCurrencyMXN(totalOutstanding)}
            icon={Banknote}
          />
          <StatCard
            title="Tasa de Morosidad"
            value={formatPercentage(delinquency.delinquency_rate_pct)}
            icon={TrendingDown}
          />
        </div>

        {/* ── Week Bucket Distribution ──────────────────────────────────── */}
        <BucketDistributionTable
          rows={delinquency.by_weeks ?? {}}
          totalOutstanding={totalOutstanding}
        />
      </div>
    </div>
  );
}
